import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import WorkRequestHandler from '../components/work-requests/WorkRequestHandler';
import FeedbackStats from '../components/work-requests/FeedbackStats';
import InterventionCalendar from '../components/work-requests/InterventionCalendar';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { COLLECTIONS } from '../firebase/collections';

const STATUS_DISPLAY = {
  pending: 'En attente', in_progress: 'En cours', completed: 'Terminé',
  cancelled: 'Annulé', pending_validation: 'En attente de validation'
};
const PRIORITY_DISPLAY = { low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };
const CATEGORY_DISPLAY = {
  plumbing: 'Plomberie', electrical: 'Électricité', electricity: 'Électricité',
  carpentry: 'Menuiserie', painting: 'Peinture', hvac: 'Climatisation',
  it: 'Informatique', cleaning: 'Nettoyage', gardening: 'Jardinage',
  locksmith: 'Serrurerie', safety: 'Sécurité / Urgence', other: 'Autre'
};
const translateCategory = (v) => v ? (CATEGORY_DISPLAY[v.toLowerCase?.()] || v) : 'Non spécifié';

const ServiceDashboard = () => {
  console.log('ServiceDashboard: Initialisation du composant');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [stats, setStats] = useState({
    byStatus: {},
    byPriority: {},
    byMonth: []
  });
  const [tabValue, setTabValue] = useState(0);
  const { currentUser, userRole, userService } = useAuth();
  
  console.log('ServiceDashboard: État initial', {
    currentUser: currentUser?.uid,
    userRole,
    userService,
    loading,
    error,
    tabValue
  });
  
  const handleTabChange = (event, newValue) => {
    console.log('ServiceDashboard: Changement d\'onglet', {
      previousValue: tabValue,
      newValue
    });
    setTabValue(newValue);
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');

      // Vérifier si l'utilisateur est autorisé
      if (!currentUser) {
        setError('Vous devez être connecté pour accéder à cette page.');
        setLoading(false);
        return;
      }

      // Autoriser l'accès aux super_admin
      if (userRole === 'super_admin') {
        // Pour les super_admin : afficher toutes les demandes
        const q = query(
          collection(db, COLLECTIONS.WORK_REQUESTS),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const requestsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || null,
          updatedAt: doc.data().updatedAt?.toDate() || null
        }));
        setRequests(requestsData);
        setLoading(false);
        return;
      }

      // Si l'utilisateur est un service demandeur, on cherche d'abord son serviceId
      let serviceId = userService?.id;
      if (!serviceId && currentUser) {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('User data:', userData);
          if (userData.serviceId) {
            serviceId = userData.serviceId;
          }
        }
      }

      console.log('ServiceId trouvé:', serviceId);

      if (!serviceId) {
        setError('Service non trouvé. Veuillez contacter votre administrateur.');
        setLoading(false);
        return;
      }

      // Récupérer les demandes en fonction du rôle
      let q;
      console.log('Informations utilisateur:', {
        role: userRole,
        userId: currentUser?.uid,
        serviceId: userService?.id
      });

      if (userRole === 'requester') {
        // Pour les demandeurs : afficher leurs demandes créées
        q = query(
          collection(db, COLLECTIONS.WORK_REQUESTS),
          where('createdBy', '==', currentUser.uid)
        );
        console.log('Requête pour demandeur avec createdBy:', currentUser.uid);
      } else if (userService?.id) {
        // Pour les services techniques : afficher les demandes qui leur sont assignées
        console.log('Service ID pour la requête:', userService.id);
        
        // Récupérer d'abord un document pour vérifier sa structure
        const testQuery = await getDocs(collection(db, COLLECTIONS.WORK_REQUESTS));
        if (!testQuery.empty) {
          console.log('Exemple de document:', testQuery.docs[0].data());
        }

        q = query(
          collection(db, COLLECTIONS.WORK_REQUESTS),
          where('assignedServiceId', '==', userService.id)
        );
        console.log('Requête pour service technique avec assignedServiceId:', userService.id);
      } else {
        setError('Service non trouvé ou non autorisé');
        setLoading(false);
        return;
      }

      const querySnapshot = await getDocs(q);
      console.log('Nombre de résultats:', querySnapshot.size);
      
      // Log chaque document pour inspection
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Document trouvé:', {
          id: doc.id,
          assignedServiceId: data.assignedServiceId,
          createdBy: data.createdBy,
          title: data.title
        });
      });

      const requestsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      console.log('Données transformées:', requestsData);

      // Calculer les statistiques
      const statusStats = {};
      const priorityStats = {};
      const monthlyStats = new Array(12).fill(0);

      requestsData.forEach(request => {
        // Statistiques par statut
        const status = request.status || 'Non défini';
        statusStats[status] = (statusStats[status] || 0) + 1;

        // Statistiques par priorité
        const priority = request.priority || 'Non défini';
        priorityStats[priority] = (priorityStats[priority] || 0) + 1;

        // Statistiques par mois
        if (request.createdAt) {
          const month = request.createdAt.getMonth();
          monthlyStats[month]++;
        }
      });

      // Préparer les données pour le graphique mensuel
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const monthlyData = monthlyStats.map((value, index) => ({
        month: monthNames[index],
        count: value
      }));

      setStats({
        byStatus: statusStats,
        byPriority: priorityStats,
        byMonth: monthlyData
      });

      setRequests(requestsData);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      setError('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('ServiceDashboard: useEffect[currentUser, userService]', {
      currentUser: currentUser?.uid,
      userRole,
      userService
    });
    fetchRequests();
  }, [currentUser, userService]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  console.log('ServiceDashboard: Rendu du composant', {
    tabValue,
    error,
    loading,
    userRole,
    userService
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Tableau de bord du service technique
      </Typography>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Demandes" />
        <Tab label="Évaluations" />
        <Tab label="Planification" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tabValue === 0 && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 300 }}>
              <Typography variant="h6" gutterBottom>
                Demandes par statut
              </Typography>
              {Object.keys(stats.byStatus).length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: Object.entries(stats.byStatus).map(([name, value]) => ({
                        id: name,
                        value,
                        label: name
                      })),
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30 },
                    },
                  ]}
                  height={250}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Aucune donnée disponible
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 300 }}>
              <Typography variant="h6" gutterBottom>
                Demandes par priorité
              </Typography>
              {Object.keys(stats.byPriority).length > 0 ? (
                <BarChart
                  dataset={Object.entries(stats.byPriority).map(([name, value]) => ({
                    priority: name,
                    count: value
                  }))}
                  yAxis={[{ scaleType: 'linear' }]}
                  xAxis={[{ scaleType: 'band', dataKey: 'priority' }]}
                  series={[{ dataKey: 'count', label: 'Nombre de demandes' }]}
                  height={250}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Aucune donnée disponible
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 300 }}>
              <Typography variant="h6" gutterBottom>
                Évolution mensuelle
              </Typography>
              {stats.byMonth.some(data => data.count > 0) ? (
                <LineChart
                  dataset={stats.byMonth}
                  xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
                  series={[{ dataKey: 'count', label: 'Nombre de demandes' }]}
                  height={250}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Aucune donnée disponible
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            Liste des demandes
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Titre</TableCell>
                  <TableCell>Service assigné</TableCell>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.createdAt?.toLocaleDateString()}</TableCell>
                    <TableCell>{request.title}</TableCell>
                    <TableCell>{request.serviceName || 'Non assigné'}</TableCell>
                    <TableCell>{translateCategory(request.category)}</TableCell>
                    <TableCell>{PRIORITY_DISPLAY[request.priority] || request.priority || 'Non spécifié'}</TableCell>
                    <TableCell>{STATUS_DISPLAY[request.status] || request.status || 'Non spécifié'}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedRequest(request)}
                      >
                        Voir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {requests.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Aucune demande trouvée
            </Typography>
          )}
        </Paper>
        </>
      )}
      
      {tabValue === 1 && (
        <FeedbackStats technicalServiceId={currentUser?.uid} />
      )}
      
      {tabValue === 2 && (
        <>
          {console.log('ServiceDashboard: Affichage de l\'onglet Planification')}
          <InterventionCalendar />
        </>
      )}
      
      {/* Suppression de l'affichage de l'erreur globale pour l'onglet planification */}
      {tabValue !== 2 && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selectedRequest && (
        <WorkRequestHandler
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdateSuccess={() => {
            setSelectedRequest(null);
            fetchRequests();
          }}
        />
      )}
    </Container>
  );
};

export default ServiceDashboard;
